// The add-on catalog as the web app needs it (ADR-0016 §2, §8): which add-ons are sold today and
// what each depends on. No endpoint serves `cp.addon` / `cp.addon_dependency` to the app, so this
// mirrors `services/api/src/seed/catalog.ts` (migration 0001 inserts the same rows); the test
// beside this file fails when the two drift. Prices are placeholders (0) and never shown.
import type { FeatureKey } from "../../api/types";

export type AddonKey = Exclude<FeatureKey, "core">;

export type AddonSpec = {
  key: AddonKey;
  name: string;
  /** One line for the Add-ons page. */
  description: string;
  /** Sold today (`cp.addon.active`): only active add-ons can be enabled (`addon_inactive`). */
  active: boolean;
  /** `bundles`: enabling this add-on brings the other along; `requires`: it must be on first. */
  dependencies: { key: AddonKey; mode: "bundles" | "requires" }[];
};

export const ADDON_CATALOG: readonly AddonSpec[] = [
  {
    key: "kpi",
    name: "KPIs and scorecards",
    description: "Metrics, targets, weekly scorecards and the Friday report.",
    active: true,
    dependencies: [],
  },
  {
    key: "memory",
    name: "Memory",
    description: "What the company knows about a person, seat or DE, beside every screen.",
    // M7-03 switched it on (migration 0021).
    active: true,
    dependencies: [],
  },
  {
    key: "agents",
    name: "Agents",
    description: "Digital employee runtime, crews and approvals.",
    active: true,
    dependencies: [{ key: "memory", mode: "bundles" }],
  },
  {
    key: "chat",
    name: "Chat",
    description: "Channels, direct messages and agent rooms.",
    active: true, // M2-02: the chat backend is sold

    dependencies: [],
  },
  {
    key: "time",
    name: "Time",
    description: "Time tracking beyond the hours entered in Setup.",
    active: false,
    dependencies: [],
  },
  {
    key: "media",
    name: "Media",
    description: "Files and assets.",
    active: false,
    dependencies: [],
  },
  {
    // WF-CONTROL: an owner or an operator switches it on (Setup › Claude Code connection).
    key: "workforce_plugin",
    name: "Workforce Control",
    description: "Manage and train the digital workforce from Claude Code.",
    active: true,
    dependencies: [{ key: "agents", mode: "requires" }],
  },
];

export function addonSpec(key: AddonKey): AddonSpec | undefined {
  return ADDON_CATALOG.find((a) => a.key === key);
}

/**
 * The feature and every add-on it depends on, transitively (`requires` and `bundles`): the same
 * closure `requireFeature` checks on the server (`featureClosure`), so the UI and the API agree.
 */
export function featureClosure(feature: FeatureKey): FeatureKey[] {
  const out: FeatureKey[] = [feature];
  for (let i = 0; i < out.length; i++) {
    const spec = ADDON_CATALOG.find((a) => a.key === out[i]);
    for (const d of spec?.dependencies ?? []) if (!out.includes(d.key)) out.push(d.key);
  }
  return out;
}

/** Add-ons that depend on `key` (they must be switched off before it). */
export function dependents(key: AddonKey): AddonSpec[] {
  return ADDON_CATALOG.filter((a) => a.dependencies.some((d) => d.key === key));
}
